/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { RouteCodes } from '@strata/constants';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { ReasonInput } from '@strata/react-compound-inputs';
import { DialogWithForm } from '@strata/react-dialog';
import { InlineMessage } from '@strata/react-inline-message';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { ServicingRate } from '@strata/react-servicing-rate';
import { Icon } from '@strata/react-status-icons';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Formik, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import { ActionRateOverride } from 'components/referrals/dialogs/ActionRateOverride';
import { handleError } from 'services/Error';
import { getConvertBiosimilarErrorMessage } from 'services/treatmentTransitionHelpers';
import {
    useGetBiosimilarConversionProductsQuery,
    useGetConfiguredProductsForPayerQuery,
    useGetEligibleSuppliersQuery,
    useLazyGetEligibleSuppliersQuery,
} from 'store/api/referenceApi';
import {
    useConvertBiosimilarMutation,
    useRunConversionCleanlinessPreviewMutation,
} from 'store/api/treatmentApi';
import { fetchReferralDetails } from 'store/referrals/referralsSlice';

const Title = 'Convert to Biosimilar';
const HOVER_CLOSE_DELAY_MS = 1000;

const ReasonOptions = [
    'Coverage exception',
    'Member preference',
    'Prescriber preference',
    'Pharmacy preference',
];

const DestinationSupplierChoices = {
    REMAIN: 'REMAIN',
    SELECT_ANOTHER: 'SELECT_ANOTHER',
    ROUTING: 'ROUTING',
};

const DestinationSupplierOptions = [
    {
        value: DestinationSupplierChoices.REMAIN,
        text: 'Remain at this pharmacy',
    },
    {
        value: DestinationSupplierChoices.SELECT_ANOTHER,
        text: 'Select another pharmacy',
    },
    {
        value: DestinationSupplierChoices.ROUTING,
        text: 'Send through routing',
    },
];

const MEDISPAN_PRICE_SOURCE = 'MEDISPAN';

// A new product or supplier means a new offer rate, so an override typed for the old one is
// stale. defaultRate is set by ActionRateOverride from the offer rate and must reset with it.
const rateOverrideResetValues = {
    ...ActionRateOverride.initialValues,
    defaultRate: undefined,
};

const initialValues = {
    destinationProduct: undefined,
    destinationSupplierChoice: undefined,
    destinationSupplierNpi: undefined,
    ...ActionRateOverride.initialValues,
    ...ReasonInput.initialValues,
};

const buildDestinationRate = (values, offerRate) => {
    const overrideRate = ActionRateOverride.submitValue(values);
    if (overrideRate === undefined || !offerRate?.referentPriceType) {
        return undefined;
    }
    return {
        rate: overrideRate,
        referent_price_type: offerRate.referentPriceType,
        price_is_frozen: false,
        price_source: MEDISPAN_PRICE_SOURCE,
    };
};

const buildDestinationSupplierNpi = (values, referralDetails) => {
    if (values.destinationSupplierChoice === DestinationSupplierChoices.REMAIN) {
        return referralDetails?.supplierNpi;
    }
    if (values.destinationSupplierChoice === DestinationSupplierChoices.SELECT_ANOTHER) {
        return values.destinationSupplierNpi;
    }
    return null;
};

const UNMANAGE_OUTCOME = 'UNMANAGE';

const PREVIEW_FAILED_DEPENDENCY_STATUS = 424;

const PREVIEW_FAILED_DEPENDENCY_MESSAGE =
    'Unable to preview conversion cleanliness for this pharmacy. Please try another pharmacy or contact support.';

const PREVIEW_FAILED_GENERIC_MESSAGE =
    'Unable to preview conversion cleanliness. Please try again.';

const previewFailureMessage = (error) =>
    error?.status === PREVIEW_FAILED_DEPENDENCY_STATUS
        ? PREVIEW_FAILED_DEPENDENCY_MESSAGE
        : PREVIEW_FAILED_GENERIC_MESSAGE;

// Server owns the cleanliness rule; the dialog only gates on the verdict (+ never convert on UNMANAGE).
const canConvertProduct = ({ wouldBeClean, wouldUnmanage }) => !!wouldBeClean && !wouldUnmanage;

const isSaveDisabledByProductChecks = ({
    isRunningPreview,
    previewFailed,
    wouldBeClean,
    wouldUnmanage,
}) => !!isRunningPreview || !!previewFailed || !canConvertProduct({ wouldBeClean, wouldUnmanage });

const canShowRemainingFields = ({
    hasDestinationProduct,
    hasDestinationSupplier,
    isRunningPreview,
    hasPreviewResponse,
    previewFailed,
}) => {
    if (!hasDestinationProduct || !hasDestinationSupplier || previewFailed) {
        return false;
    }
    return !isRunningPreview && hasPreviewResponse;
};

const claimsWereEvaluated = (previewData) =>
    previewData?.payableProductServiceId != null ||
    previewData?.disposition != null ||
    previewData?.outcome != null ||
    previewData?.cleanReason != null ||
    previewData?.rejectCode != null;

const buildRerouteWarning = ({
    rerouteDuringRouteSim,
    previewSupplierNpi,
    selectedSupplierNpi,
    eligibleSuppliers,
}) => {
    if (!rerouteDuringRouteSim || !previewSupplierNpi || !selectedSupplierNpi) {
        return undefined;
    }

    const pharmacyName =
        (eligibleSuppliers ?? []).find(({ npi }) => npi === previewSupplierNpi)?.name ??
        previewSupplierNpi;

    return `This conversion will cause the referral to be rerouted to ${pharmacyName} (${previewSupplierNpi}).`;
};

const buildNonROFRWarning = (routeCode) => {
    if (!routeCode || routeCode === RouteCodes.ROFR) {
        return undefined;
    }
    return `This conversion will cause the referral to be sent with the ${routeCode} route code.`;
};

const buildBlocklistedProductStatus = () => (
    <span className="sds_convert-biosimilar-dialog__test-claim-status">
        <InlineMessage intent={InlineMessage.Intent.ERROR} label="Blocklisted Treatment" />
    </span>
);

const buildPreviewStatusLabel = ({ previewData, evaluatedClaims }) => {
    const { payableProductServiceId, hasActiveAuth } = previewData;
    if (evaluatedClaims) {
        return `${payableProductServiceId ? 'Payable' : 'Not payable'}, ${
            hasActiveAuth ? 'PA on file' : 'no PA on file'
        }`;
    }
    return `${hasActiveAuth ? 'PA on file' : 'No PA on file'}`;
};

const resolvePreviewStatusIntent = ({ canConvert, evaluatedClaims, payableProductServiceId }) => {
    if (!canConvert) {
        return InlineMessage.Intent.ERROR;
    }
    // A PA (or other non-claim path) rescuing an unpayable claim still converts, but warrants caution.
    if (!evaluatedClaims || payableProductServiceId) {
        return InlineMessage.Intent.SUCCESS;
    }
    return InlineMessage.Intent.WARNING;
};

const buildEvaluatedProductStatus = ({ previewData, selectedSupplierNpi, eligibleSuppliers }) => {
    const canConvert = canConvertProduct({
        wouldBeClean: previewData.wouldBeClean,
        wouldUnmanage: previewData.wouldUnmanage || previewData.outcome === UNMANAGE_OUTCOME,
    });
    const evaluatedClaims = claimsWereEvaluated(previewData);
    const label = buildPreviewStatusLabel({ previewData, evaluatedClaims });
    const intent = resolvePreviewStatusIntent({
        canConvert,
        evaluatedClaims,
        payableProductServiceId: previewData.payableProductServiceId,
    });
    const rerouteWarning = buildRerouteWarning({
        rerouteDuringRouteSim: previewData.rerouteDuringRouteSim,
        previewSupplierNpi: previewData.supplierNpi,
        selectedSupplierNpi,
        eligibleSuppliers,
    });
    const nonROFRWarning = buildNonROFRWarning(previewData?.routeCode);

    return (
        <>
            {rerouteWarning && (
                <div className="sds_convert-biosimilar-dialog__reroute-message">
                    <InlineMessage
                        intent={InlineMessage.Intent.WARNING}
                        label={rerouteWarning}
                        wrapText
                    />
                </div>
            )}
            {nonROFRWarning && (
                <div className="sds_convert-biosimilar-dialog__non-rofr-message">
                    <InlineMessage
                        intent={InlineMessage.Intent.WARNING}
                        label={nonROFRWarning}
                        wrapText
                    />
                </div>
            )}
            <span className="sds_convert-biosimilar-dialog__test-claim-status">
                <InlineMessage intent={intent} label={label} />
            </span>
        </>
    );
};

const buildProductStatus = ({
    enabled,
    isRunningPreview,
    previewData,
    previewFailed,
    previewError = undefined,
    destinationProductValue,
    hasDestinationSupplier,
    selectedSupplierNpi = undefined,
    eligibleSuppliers = undefined,
}) => {
    if (!enabled || !hasDestinationSupplier || !destinationProductValue) {
        return undefined;
    }

    if (isRunningPreview) {
        return (
            <span className="sds_convert-biosimilar-dialog__test-claim-status">
                <span className="sds_ellipsis-animation">Evaluating</span>
            </span>
        );
    }

    if (previewFailed) {
        return (
            <span className="sds_convert-biosimilar-dialog__test-claim-status">
                <InlineMessage
                    intent={InlineMessage.Intent.ERROR}
                    label={previewFailureMessage(previewError)}
                />
            </span>
        );
    }

    // Wait until the preview answers before showing a dirty/clean status.
    if (previewData === undefined) {
        return undefined;
    }

    if (previewData.dirtyReason === 'BLOCKLISTED') {
        return buildBlocklistedProductStatus();
    }

    return buildEvaluatedProductStatus({
        previewData,
        selectedSupplierNpi,
        eligibleSuppliers,
    });
};

const buildEligibleSuppliersArgs = (destinationProduct, referralDetails, memberDetails) => ({
    payerId: referralDetails.payerId,
    segmentId: referralDetails.segmentId,
    memberState: memberDetails.memberState,
    productServiceOid: destinationProduct.value,
    benefit: referralDetails.benefit,
    placeOfService: referralDetails.placeOfService,
    isBrand: destinationProduct.is_brand,
    lineOfBusiness: memberDetails.lineOfBusiness,
    lobSegment: memberDetails.lobSegment,
});

const useDestinationEligibleSuppliers = ({
    referralDetails = undefined,
    memberDetails = undefined,
    destinationProduct = undefined,
}) =>
    useGetEligibleSuppliersQuery(
        destinationProduct && memberDetails
            ? buildEligibleSuppliersArgs(destinationProduct, referralDetails, memberDetails)
            : skipToken,
    );

const DestinationSupplierRadio = ({
    referralDetails = undefined,
    memberDetails = undefined,
    destinationProduct = undefined,
    onDestinationSupplierChange,
    isRunningPreview,
    previewData = undefined,
    previewFailed = false,
    previewError = undefined,
}) => {
    const formik = useFormikContext();
    const currentSupplierNpi = referralDetails?.supplierNpi;
    const { data: eligibleSuppliers, isFetching } = useDestinationEligibleSuppliers({
        referralDetails,
        memberDetails,
        destinationProduct,
    });

    const currentSupplierIsEligible = (eligibleSuppliers ?? [])
        .map(({ npi }) => npi)
        .includes(currentSupplierNpi);

    const isRemainEnabled = !destinationProduct || (!isFetching && currentSupplierIsEligible);
    const showIneligibleMessage = destinationProduct && !isFetching && !currentSupplierIsEligible;
    const selectedSupplierNpi = buildDestinationSupplierNpi(formik.values, referralDetails);
    const productStatus = buildProductStatus({
        enabled:
            formik.values.destinationSupplierChoice === DestinationSupplierChoices.REMAIN ||
            formik.values.destinationSupplierChoice === DestinationSupplierChoices.ROUTING,
        isRunningPreview,
        previewData,
        previewFailed,
        previewError,
        destinationProductValue: destinationProduct?.value,
        hasDestinationSupplier:
            formik.values.destinationSupplierChoice === DestinationSupplierChoices.ROUTING ||
            !!selectedSupplierNpi,
        selectedSupplierNpi,
        eligibleSuppliers,
    });

    return (
        <RadioButtonGroupFormik
            label="Destination Supplier"
            required
            options={[
                {
                    ...DestinationSupplierOptions[0],
                    disabled: !isRemainEnabled,
                },
                DestinationSupplierOptions[1],
                DestinationSupplierOptions[2],
            ]}
            name="destinationSupplierChoice"
            selectedValue={formik.values.destinationSupplierChoice}
            onChange={(newVal) => {
                formik.setValues({
                    ...formik.values,
                    ...rateOverrideResetValues,
                    destinationSupplierChoice: newVal,
                    destinationSupplierNpi:
                        newVal === DestinationSupplierChoices.SELECT_ANOTHER
                            ? formik.values.destinationSupplierNpi
                            : undefined,
                });
                onDestinationSupplierChange(
                    newVal === DestinationSupplierChoices.REMAIN ? currentSupplierNpi : undefined,
                    newVal,
                );
            }}
            helperText={
                showIneligibleMessage || productStatus ? (
                    <>
                        {showIneligibleMessage && (
                            <div className="sds_convert-biosimilar-dialog__ineligible-message">
                                <InlineMessage
                                    intent={InlineMessage.Intent.WARNING}
                                    label="Current pharmacy is unable to supply selected product."
                                />
                            </div>
                        )}
                        {productStatus}
                    </>
                ) : undefined
            }
        />
    );
};

DestinationSupplierRadio.propTypes = {
    referralDetails: PropTypes.shape({
        payerId: PropTypes.string,
        segmentId: PropTypes.string,
        benefit: PropTypes.string,
        placeOfService: PropTypes.string,
        supplierNpi: PropTypes.string,
    }),
    memberDetails: PropTypes.shape({
        memberState: PropTypes.string,
        lineOfBusiness: PropTypes.string,
        lobSegment: PropTypes.string,
    }),
    destinationProduct: PropTypes.shape({
        value: PropTypes.string,
        is_brand: PropTypes.bool,
    }),
    onDestinationSupplierChange: PropTypes.func.isRequired,
    isRunningPreview: PropTypes.bool.isRequired,
    previewData: PropTypes.shape({}),
    previewFailed: PropTypes.bool,
    previewError: PropTypes.shape({
        status: PropTypes.number,
    }),
};

const DestinationSupplierSelect = ({
    referralDetails = undefined,
    memberDetails = undefined,
    onDestinationSupplierChange,
    isRunningPreview,
    previewData = undefined,
    previewFailed = false,
    previewError = undefined,
}) => {
    const formik = useFormikContext();
    const { destinationProduct, destinationSupplierChoice } = formik.values;
    const currentSupplierNpi = referralDetails?.supplierNpi;

    const { data: eligibleSuppliers, isFetching } = useDestinationEligibleSuppliers({
        referralDetails,
        memberDetails,
        destinationProduct,
    });

    const supplierOptions = (eligibleSuppliers ?? []).filter(
        ({ npi }) => npi !== currentSupplierNpi,
    );

    if (
        !destinationProduct ||
        destinationSupplierChoice !== DestinationSupplierChoices.SELECT_ANOTHER
    ) {
        return null;
    }

    return (
        <SuggestSingleSelectFormik
            id="destinationSupplierNpi"
            label="Pharmacy"
            required
            values={supplierOptions}
            isLoading={isFetching}
            selectedItem={supplierOptions.find(
                ({ npi }) => npi === formik.values.destinationSupplierNpi,
            )}
            onChange={(newValue) => {
                formik.setValues({
                    ...formik.values,
                    ...rateOverrideResetValues,
                    destinationSupplierNpi: newValue?.npi,
                });
                onDestinationSupplierChange(newValue?.npi);
            }}
            helperText={buildProductStatus({
                enabled: true,
                isRunningPreview,
                previewData,
                previewFailed,
                previewError,
                destinationProductValue: formik.values.destinationProduct?.value,
                hasDestinationSupplier: !!formik.values.destinationSupplierNpi,
                selectedSupplierNpi: formik.values.destinationSupplierNpi,
                eligibleSuppliers,
            })}
        />
    );
};

DestinationSupplierSelect.propTypes = {
    referralDetails: PropTypes.shape({
        payerId: PropTypes.string,
        segmentId: PropTypes.string,
        benefit: PropTypes.string,
        placeOfService: PropTypes.string,
        supplierNpi: PropTypes.string,
    }),
    memberDetails: PropTypes.shape({
        memberState: PropTypes.string,
        lineOfBusiness: PropTypes.string,
        lobSegment: PropTypes.string,
    }),
    onDestinationSupplierChange: PropTypes.func.isRequired,
    isRunningPreview: PropTypes.bool.isRequired,
    previewData: PropTypes.shape({}),
    previewFailed: PropTypes.bool,
    previewError: PropTypes.shape({
        status: PropTypes.number,
    }),
};

const buildReferralRouteCheckArgs = ({
    destinationProduct,
    destinationSupplierNpi,
    marketplaceParticipants,
    therapeuticCategory,
    referralDetails,
    memberDetails,
}) => ({
    payerId: referralDetails.payerId,
    segmentId: referralDetails.segmentId,
    memberId: memberDetails.memberId,
    patientFirstName: memberDetails.firstName,
    patientDob: memberDetails.dateOfBirth,
    productServiceOid: destinationProduct.value,
    isBrand: destinationProduct.is_brand,
    benefit: referralDetails.benefit,
    memberBenefitGroup: memberDetails.benefitGroup ?? null,
    patientZip: memberDetails.memberZip,
    supplierNpi: destinationSupplierNpi ?? null,
    offerRate: null,
    prescriberNpi: referralDetails.prescriberNpi,
    marketplaceParticipants,
    placeOfService: referralDetails.placeOfService,
    therapeuticCategory,
    preferredPharmacyOffer: destinationSupplierNpi
        ? {
              parentPharmacyNpi: destinationSupplierNpi,
              routeCode: RouteCodes.ROFR,
              allowRouteOverride: true,
          }
        : null,
});

const offerRatePropType = PropTypes.shape({
    rate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    referentPriceType: PropTypes.string,
});

const RateFields = ({ offerRate }) => {
    // ActionRateOverride compares the override against a rate rule's default rate.
    const overrideRateRule = useMemo(
        () => ({
            defaultRate: offerRate.rate,
            referentPriceType: offerRate.referentPriceType,
        }),
        [offerRate],
    );

    return (
        <ActionRateOverride
            label="Offer Rate Override"
            helperText="This will override the offer rate, unless rate entered is less competitive than the Place of Service Override Rate."
            rateRule={overrideRateRule}
        />
    );
};

RateFields.propTypes = {
    offerRate: offerRatePropType.isRequired,
};

const OfferRateDisplay = ({
    offerRate = undefined,
    routeCode = undefined,
    routeRuleName = undefined,
}) => (
    <DetailLabelValuePair
        className="margin__bottom--medium"
        label={
            <span className="flex__gap--small">
                Offer Rate
                {routeCode && (
                    <Icon
                        icon={IconNames.INFO_SIGN}
                        size={12}
                        intent={Icon.Intent.INFO}
                        title="Route Simulation Info"
                        hoverCloseDelay={HOVER_CLOSE_DELAY_MS}
                        tooltipContent={
                            <>
                                <div>
                                    <span className="font-bold">Route Code: </span>
                                    <span>{routeCode}</span>
                                </div>
                                <div>
                                    <span className="font-bold">Route Rule: </span>
                                    <span>{routeRuleName}</span>
                                </div>
                            </>
                        }
                    />
                )}
            </span>
        }
        value={
            <ServicingRate
                servicingRate={offerRate?.rate}
                referentPriceType={offerRate?.referentPriceType}
            />
        }
    />
);

OfferRateDisplay.propTypes = {
    offerRate: offerRatePropType,
    routeCode: PropTypes.string,
    routeRuleName: PropTypes.string,
};

export const ConvertBiosimilarDialog = ({
    isOpen = false,
    onClose,
    referralDetails = undefined,
    memberDetails = undefined,
}) => {
    const dispatch = useDispatch();
    const { data: biosimilarConversionOptions, isFetching: biosimilarConversionOptionsIsFetching } =
        useGetBiosimilarConversionProductsQuery(
            referralDetails?.productServiceOid ? referralDetails.productServiceOid : skipToken,
        );
    const { data: configuredProducts, isFetching: configuredProductsIsFetching } =
        useGetConfiguredProductsForPayerQuery(
            referralDetails?.segmentId ? { segmentId: referralDetails.segmentId } : skipToken,
        );
    const [convertBiosimilar] = useConvertBiosimilarMutation();
    const [
        runConversionCleanlinessPreview,
        {
            isLoading: isRunningPreview,
            data: previewData,
            error: previewError,
            isError: previewFailed,
            reset: resetConversionCleanlinessPreview,
        },
    ] = useRunConversionCleanlinessPreviewMutation();
    const [getEligibleSuppliers] = useLazyGetEligibleSuppliersQuery();

    const offerRate = useMemo(() => {
        const rate = previewData?.offerRate;
        if (rate === undefined || rate === null) {
            return undefined;
        }

        return { rate, referentPriceType: previewData.referentPriceType };
    }, [previewData]);

    const validationSchema = Yup.object({
        destinationProduct: Yup.object({
            name: Yup.string(),
            value: Yup.string(),
            is_brand: Yup.bool(),
        }).required(validationStrings.requiredField.errorMessage),
        destinationSupplierChoice: Yup.string()
            .oneOf(Object.values(DestinationSupplierChoices))
            .required(validationStrings.requiredField.errorMessage),
        destinationSupplierNpi: Yup.string().when('destinationSupplierChoice', {
            is: DestinationSupplierChoices.SELECT_ANOTHER,
            then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
        }),
        ...ActionRateOverride.validationSchema,
        ...ReasonInput.validationSchema,
    });

    const previewRunRef = useRef(0);

    const resetPreview = () => {
        previewRunRef.current += 1;
        resetConversionCleanlinessPreview();
    };

    const handleClose = (resetForm) => {
        resetPreview();
        resetForm();
        onClose();
    };

    const runConversionCleanliness = async (product, parentSupplierNpi) => {
        if (!product || !referralDetails || !memberDetails) {
            resetPreview();
            return;
        }

        resetPreview();
        const runId = previewRunRef.current;

        // Routing names the pharmacy only after conversion, so there is no route to simulate.
        // A named pharmacy sends the route parameters; treatment runs the simulation.
        let routeCheck;
        if (parentSupplierNpi) {
            const { data: eligibleSuppliers, isError } = await getEligibleSuppliers(
                buildEligibleSuppliersArgs(product, referralDetails, memberDetails),
            );
            if (runId !== previewRunRef.current) {
                return;
            }
            if (!isError) {
                const therapeuticCategory = configuredProducts?.find(
                    ({ oid }) => oid === product.value,
                )?.therapeuticCategory;
                routeCheck = buildReferralRouteCheckArgs({
                    destinationProduct: product,
                    destinationSupplierNpi: parentSupplierNpi,
                    marketplaceParticipants: (eligibleSuppliers ?? []).map(({ npi }) => npi),
                    therapeuticCategory,
                    referralDetails,
                    memberDetails,
                });
            }
        }

        try {
            await runConversionCleanlinessPreview({
                payerId: referralDetails.payerId,
                segmentId: referralDetails.segmentId,
                memberId: memberDetails.memberId,
                patientFirstName: memberDetails.firstName,
                patientLastName: memberDetails.lastName,
                patientDob: memberDetails.dateOfBirth,
                prescriberNpi: referralDetails.prescriberNpi,
                productServiceIds: product.productServiceIds ?? [],
                productServiceOid: product.value,
                asOfDate: formatDate(dayjs(), DateFormats.HYPHEN_DATE),
                parentSupplierNpi,
                treatmentId: referralDetails.treatmentId,
                referralId: referralDetails.referralId,
                lineOfBusiness: memberDetails.lineOfBusiness,
                routeCheck,
            }).unwrap();
        } catch (err) {
            if (runId !== previewRunRef.current) {
                return;
            }
            handleError(previewFailureMessage(err), err);
        }
    };

    const handleDestinationProductChange = (product, formik) => {
        formik.setValues({
            ...formik.values,
            destinationProduct: product,
            destinationSupplierChoice: undefined,
            destinationSupplierNpi: undefined,
            ...rateOverrideResetValues,
        });
        resetPreview();
    };

    const handleDestinationSupplierChange = (
        supplierNpi,
        formik,
        destinationSupplierChoice = formik.values.destinationSupplierChoice,
    ) => {
        const product = formik.values.destinationProduct;
        if (destinationSupplierChoice === DestinationSupplierChoices.ROUTING) {
            // Routing names the pharmacy only after conversion, so there is no parent NPI to
            // resolve a billing supplier from; treatment falls back to its claim check NPI.
            runConversionCleanliness(product);
            return;
        }
        if (!supplierNpi) {
            resetPreview();
            return;
        }
        runConversionCleanliness(product, supplierNpi);
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            validateOnMount
            onSubmit={async (values, { resetForm }) => {
                if (!referralDetails) {
                    return;
                }
                const { payerId, treatmentId, referralId } = referralDetails;
                try {
                    const destinationRate = buildDestinationRate(values, offerRate);
                    const destinationSupplierNpi = buildDestinationSupplierNpi(
                        values,
                        referralDetails,
                    );
                    await convertBiosimilar({
                        payerId,
                        treatmentId,
                        body: {
                            destination_product_service_oid: values.destinationProduct.value,
                            ...(previewData?.payableProductServiceId && {
                                destination_product_service_id: previewData.payableProductServiceId,
                            }),
                            destination_supplier_npi: destinationSupplierNpi,
                            // Preview rate is only stored/usable when a destination pharmacy is named.
                            ...(previewData?.conversionPreviewId &&
                                destinationSupplierNpi && {
                                    conversion_preview_id: previewData.conversionPreviewId,
                                }),
                            ...(destinationRate && { destination_rate: destinationRate }),
                            destination_is_brand: values.destinationProduct.is_brand,
                            ...ReasonInput.submitValues(values),
                        },
                    }).unwrap();
                    dispatch(fetchReferralDetails({ payerId, referralId }));
                    SuccessToast({ message: 'Biosimilar conversion initiated successfully.' });
                    handleClose(resetForm);
                } catch (err) {
                    handleError(getConvertBiosimilarErrorMessage(err), err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    isOpen={isOpen}
                    dialogTitle={Title}
                    width={560}
                    isPrimaryButtonDisabled={
                        !(formik.dirty && formik.isValid) ||
                        formik.isSubmitting ||
                        isSaveDisabledByProductChecks({
                            isRunningPreview,
                            previewFailed,
                            wouldBeClean: previewData?.wouldBeClean,
                            wouldUnmanage:
                                previewData?.wouldUnmanage ||
                                previewData?.outcome === UNMANAGE_OUTCOME,
                        })
                    }
                    onSecondaryButtonClick={() => handleClose(formik.resetForm)}
                    primaryButtonLabel="Save"
                    secondaryButtonLabel="Cancel"
                    dialogContent={
                        <>
                            <SuggestSingleSelectFormik
                                id="destinationProduct"
                                label="Product"
                                required
                                disableAutoPopulate
                                values={biosimilarConversionOptions}
                                isLoading={
                                    biosimilarConversionOptionsIsFetching ||
                                    configuredProductsIsFetching
                                }
                                selectedItem={formik.values.destinationProduct}
                                itemsEqual={(a, b) =>
                                    a?.value === b?.value && a?.is_brand === b?.is_brand
                                }
                                onChange={(product) =>
                                    handleDestinationProductChange(product, formik)
                                }
                            />
                            {formik.values.destinationProduct && (
                                <>
                                    <DestinationSupplierRadio
                                        referralDetails={referralDetails}
                                        memberDetails={memberDetails}
                                        destinationProduct={formik.values.destinationProduct}
                                        onDestinationSupplierChange={(supplierNpi, choice) =>
                                            handleDestinationSupplierChange(
                                                supplierNpi,
                                                formik,
                                                choice,
                                            )
                                        }
                                        isRunningPreview={isRunningPreview}
                                        previewData={previewData}
                                        previewFailed={previewFailed}
                                        previewError={previewError}
                                    />
                                    <DestinationSupplierSelect
                                        referralDetails={referralDetails}
                                        memberDetails={memberDetails}
                                        onDestinationSupplierChange={(supplierNpi) =>
                                            handleDestinationSupplierChange(supplierNpi, formik)
                                        }
                                        isRunningPreview={isRunningPreview}
                                        previewData={previewData}
                                        previewFailed={previewFailed}
                                        previewError={previewError}
                                    />
                                </>
                            )}
                            {canShowRemainingFields({
                                hasDestinationProduct: !!formik.values.destinationProduct,
                                hasDestinationSupplier:
                                    formik.values.destinationSupplierChoice ===
                                        DestinationSupplierChoices.ROUTING ||
                                    !!buildDestinationSupplierNpi(formik.values, referralDetails),
                                isRunningPreview,
                                hasPreviewResponse: previewData !== undefined,
                                previewFailed,
                            }) && (
                                <>
                                    {formik.values.destinationSupplierChoice !==
                                        DestinationSupplierChoices.ROUTING && (
                                        <>
                                            <OfferRateDisplay
                                                offerRate={offerRate}
                                                routeCode={previewData?.routeCode}
                                                routeRuleName={previewData?.routeRuleName}
                                            />
                                            {/* No offer rate means no referent price type to send an override with. */}
                                            {offerRate?.referentPriceType && (
                                                <RateFields offerRate={offerRate} />
                                            )}
                                        </>
                                    )}
                                    <ReasonInput reasons={ReasonOptions} />
                                    <InlineMessage
                                        intent={InlineMessage.Intent.WARNING}
                                        label="The current referral will be expired and a new one will be created."
                                        wrapText
                                    />
                                </>
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};

ConvertBiosimilarDialog.Title = Title;

ConvertBiosimilarDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    referralDetails: PropTypes.shape({
        referralId: PropTypes.string.isRequired,
        payerId: PropTypes.string.isRequired,
        treatmentId: PropTypes.string.isRequired,
        segmentId: PropTypes.string,
        productServiceOid: PropTypes.string,
        prescriberNpi: PropTypes.string,
        supplierNpi: PropTypes.string,
        benefit: PropTypes.string,
        placeOfService: PropTypes.string,
    }),
    memberDetails: PropTypes.shape({
        memberId: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        memberState: PropTypes.string,
        memberZip: PropTypes.string,
        benefitGroup: PropTypes.string,
        lineOfBusiness: PropTypes.string,
        lobSegment: PropTypes.string,
        dateOfBirth: PropTypes.string,
    }),
};
